import { safeJson } from "@/lib/http";
import { supabase } from "@/lib/supabase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ia`;

async function authHeaders() {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Session expirée, reconnectez-vous.");
    return { Authorization: `Bearer ${token}` };
}

export async function fetchIaSessions(dossierId) {
    const r = await fetch(`${API}/dossiers/${dossierId}/sessions`, { headers: await authHeaders() });
    if (!r.ok) throw new Error((await safeJson(r)).detail || "Erreur de chargement");
    return safeJson(r);
}

export async function fetchIaSessionDetail(dossierId, sessionId) {
    const r = await fetch(`${API}/dossiers/${dossierId}/sessions/${sessionId}`, { headers: await authHeaders() });
    if (!r.ok) throw new Error((await safeJson(r)).detail || "Erreur de chargement");
    return safeJson(r);
}

export async function deleteIaSession(dossierId, sessionId) {
    const r = await fetch(`${API}/dossiers/${dossierId}/sessions/${sessionId}`, { method: "DELETE", headers: await authHeaders() });
    if (!r.ok) throw new Error("Suppression impossible");
}

export async function updateSynthese(dossierId, sessionId, content) {
    const r = await fetch(`${API}/dossiers/${dossierId}/sessions/${sessionId}/synthese`, {
        method: "PUT", headers: { ...(await authHeaders()), "Content-Type": "application/json" }, body: JSON.stringify({ content }),
    });
    if (!r.ok) throw new Error((await safeJson(r)).detail || "Enregistrement impossible");
    return safeJson(r);
}

export async function downloadSynthesePdf(dossierId, sessionId) {
    const r = await fetch(`${API}/dossiers/${dossierId}/sessions/${sessionId}/synthese.pdf`, { headers: await authHeaders() });
    if (!r.ok) throw new Error((await safeJson(r)).detail || "Export impossible");
    const cd = r.headers.get("Content-Disposition") || "";
    const filename = cd.match(/filename="?([^";]+)"?/)?.[1] || "Synthese.pdf";
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return filename;
}

export async function fetchInvestigation(dossierId) {
    const r = await fetch(`${API}/dossiers/${dossierId}/investigation`, { headers: await authHeaders() });
    if (!r.ok) throw new Error((await safeJson(r)).detail || "Erreur de chargement");
    return safeJson(r);
}

export async function resetInvestigation(dossierId) {
    const r = await fetch(`${API}/dossiers/${dossierId}/investigation`, { method: "DELETE", headers: await authHeaders() });
    if (!r.ok) throw new Error("Réinitialisation impossible");
}

async function readSse(r, { onDelta, onDone, onError, onStatus }) {
    if (!r.ok) {
        const j = await safeJson(r);
        onError(j.detail || `Erreur ${r.status}`);
        return;
    }
    const reader = r.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
            if (!p.startsWith("data: ")) continue;
            let ev;
            try { ev = JSON.parse(p.slice(6)); } catch { continue; }
            if (ev.error) {
                await reader.cancel().catch(() => {});
                onError(ev.error);
                return;
            }
            if (ev.delta) onDelta(ev.delta, ev.stage);
            else if (ev.stage) onStatus?.(ev.stage);
            else if (ev.status) onStatus?.(ev.status);
            else if (ev.done) onDone(ev);
        }
    }
}

export async function streamRapportDouble(dossierId, handlers, signal) {
    const r = await fetch(`${API}/dossiers/${dossierId}/rapport-double`, { method: "POST", headers: await authHeaders(), signal });
    await readSse(r, handlers);
}

export async function fetchRapportDouble(dossierId) {
    const r = await fetch(`${API}/dossiers/${dossierId}/rapport-double`, { headers: await authHeaders() });
    if (!r.ok) throw new Error((await safeJson(r)).detail || "Erreur de chargement");
    return safeJson(r);
}

export async function startRapportDoubleJob(dossierId) {
    const r = await fetch(`${API}/dossiers/${dossierId}/rapport-double/jobs`, { method: "POST", headers: await authHeaders() });
    if (!r.ok) throw new Error((await safeJson(r)).detail || "Lancement impossible");
    return safeJson(r);
}

export async function fetchRapportDoubleJob(dossierId, jobId) {
    const r = await fetch(`${API}/dossiers/${dossierId}/rapport-double/jobs/${jobId}`, { headers: await authHeaders() });
    if (!r.ok) throw new Error((await safeJson(r)).detail || "Job introuvable");
    return safeJson(r);
}

export async function fetchRapportDoubleHistorique(dossierId) {
    const r = await fetch(`${API}/dossiers/${dossierId}/rapport-double/historique`, { headers: await authHeaders() });
    if (!r.ok) throw new Error((await safeJson(r)).detail || "Erreur de chargement");
    return safeJson(r);
}

export async function fetchRapportDoubleById(dossierId, sessionId) {
    const r = await fetch(`${API}/dossiers/${dossierId}/rapport-double/${sessionId}`, { headers: await authHeaders() });
    if (!r.ok) throw new Error((await safeJson(r)).detail || "Erreur de chargement");
    return safeJson(r);
}

export async function downloadRapportDoublePdf(dossierId, sessionId, kind) {
    const r = await fetch(`${API}/dossiers/${dossierId}/rapport-double/${sessionId}/pdf?kind=${kind}`, { headers: await authHeaders() });
    if (!r.ok) throw new Error((await safeJson(r)).detail || "Export PDF impossible");
    const name = /filename="([^"]+)"/.exec(r.headers.get("Content-Disposition") || "")?.[1] || `${kind}.pdf`;
    const url = URL.createObjectURL(await r.blob());
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    a.click();
    URL.revokeObjectURL(url);
    return name;
}

async function pollJob(dossierId, r, { onDelta, onDone, onError, onStatus }, signal) {
    if (!r.ok) {
        const j = await safeJson(r);
        onError(j.detail || `Erreur ${r.status}`);
        return;
    }
    const { job_id } = await r.json();
    let last = "";
    for (;;) {
        if (signal?.aborted) throw Object.assign(new Error("aborted"), { name: "AbortError" });
        await new Promise((res) => setTimeout(res, 3000));
        const jr = await fetch(`${API}/dossiers/${dossierId}/jobs/${job_id}`, { headers: await authHeaders(), signal });
        if (!jr.ok) { onError((await safeJson(jr)).detail || `Erreur ${jr.status}`); return; }
        const j = await jr.json();
        if (j.stage !== last) { last = j.stage; onStatus?.(j.stage); }
        if (j.stage === "error") { onError(j.error || "Génération IA en erreur"); return; }
        if (j.stage === "done") { onDelta(j.content || ""); onDone(j); return; }
    }
}

export async function streamInvestigation(dossierId, { message, syntheseId }, handlers) {
    const q = syntheseId ? `&synthese_id=${encodeURIComponent(syntheseId)}` : "";
    const r = await fetch(`${API}/dossiers/${dossierId}/investigation?mode=job${q}`, {
        method: "POST", headers: { ...(await authHeaders()), "Content-Type": "application/json" }, body: JSON.stringify({ message }),
    });
    await pollJob(dossierId, r, handlers);
}

export async function streamIntegration(dossierId, sessionId, handlers) {
    const r = await fetch(`${API}/dossiers/${dossierId}/sessions/${sessionId}/integrer?mode=job`, { method: "POST", headers: await authHeaders() });
    await pollJob(dossierId, r, handlers);
}

export async function streamIa(dossierId, { message, typeMandat, sessionId } = {}, handlers, signal) {
    const url = `${API}/dossiers/${dossierId}/${message ? `chat?mode=job&session_id=${encodeURIComponent(sessionId || "")}` : `synthese?mode=job&type_mandat=${encodeURIComponent(typeMandat || "audit_financier")}`}`;
    const r = await fetch(url, {
        method: "POST",
        headers: { ...(await authHeaders()), "Content-Type": "application/json" },
        body: message ? JSON.stringify({ message }) : undefined,
        signal,
    });
    await pollJob(dossierId, r, handlers, signal);
}
